import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebaseConfig';
import { MeiliSearch } from 'meilisearch';
import TopNavbar from '../../components/TopNavbar';
import SideNavbar from '../../components/SideNavbar';
import { collection, doc, getDoc, getDocs, query, updateDoc, where, limit, orderBy, Timestamp } from 'firebase/firestore';
import Toast from '../../components/Toast';
import { useAppSelector } from '../../redux/store';

interface ProgramType {
  id: string;
  programType: string;
}

interface Country {
  id: string;
  name: string;
}

interface University {
  id: string;
  name: string;
  uniCode: string;
}

interface RelationshipManager {
  name?: string;
  email?: string;
  phone?: string;
  linkedin?: string;
}

interface LeadAdmissionStatus {
  admissionStatus: string;
}

interface Task {
  id: string;
  task: string;
  isDone: boolean;
  dueDate: Timestamp;
  createdAt: Timestamp;
  userId: string;
}

const StudentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);
  const [activeQuickAction, setActiveQuickAction] = useState<string>("Pending Tasks");
  const [relationshipManager, setRelationshipManager] = useState<RelationshipManager | null>(null);
  const [admissionStatus, setAdmissionStatus] = useState<string | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  // Program Type states
  const [programTypes, setProgramTypes] = useState<ProgramType[]>([]);
  const [selectedProgramType, setSelectedProgramType] = useState<string>('');
  const [selectedProgramName, setSelectedProgramName] = useState<string>('');
  const [isProgramOpen, setIsProgramOpen] = useState(false);

  // Country states
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedCountryName, setSelectedCountryName] = useState<string>('');
  const [isLoadingCountry, setIsLoadingCountry] = useState(false);
  const [isDestinationOpen, setIsDestinationOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  // University states
  const [universities, setUniversities] = useState<University[]>([]);
  const [selectedUniversities, setSelectedUniversities] = useState<University[]>([]);
  const [selectedUniversityIds, setSelectedUniversityIds] = useState<string[]>([]);
  const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
  const [isUniversityDropdownOpen, setIsUniversityDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Intake states
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);

  // Task states
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isUpdatingTask, setIsUpdatingTask] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isErrorToast, setIsErrorToast] = useState(false);
  const [showToast, setShowToast] = useState(false);

  // Refs
  const programRef = useRef<HTMLDivElement>(null);
  const destinationRef = useRef<HTMLDivElement>(null);
  const universitiesRef = useRef<HTMLDivElement>(null);
  const universitiesInputRef = useRef<HTMLInputElement>(null);
  const monthDropdownRef = useRef<HTMLDivElement>(null);
  const yearDropdownRef = useRef<HTMLDivElement>(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from(
    { length: 11 },
    (_, i) => 2020 + i
  );

  const QuickActions = ["Pending Tasks", "Open Tickets", "Open Escalations"]

  const handleQuickActionClick = (action: string) => {
    setActiveQuickAction(action);
    switch (action) {
      case "Pending Tasks":
        navigate("/pending-tasks");
        break;
      case "Open Tickets":
        navigate("/open-tickets");
        break;
      case "Open Escalations":
        navigate("/open-escalations");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (programRef.current && !programRef.current.contains(event.target as Node)) {
        setIsProgramOpen(false);
      }
      if (destinationRef.current && !destinationRef.current.contains(event.target as Node)) {
        setIsDestinationOpen(false);
      }
      if (
        universitiesRef.current &&
        universitiesInputRef.current &&
        !universitiesRef.current.contains(event.target as Node) &&
        !universitiesInputRef.current.contains(event.target as Node)
      ) {
        setIsUniversityDropdownOpen(false);
      }
      if (
        monthDropdownRef.current &&
        !monthDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMonthDropdownOpen(false);
      }
      if (
        yearDropdownRef.current &&
        !yearDropdownRef.current.contains(event.target as Node)
      ) {
        setIsYearDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        // Get user claims
        const idTokenResult = await currentUser.getIdTokenResult();
        const leadId = idTokenResult.claims.leadUid;
        const companyId = idTokenResult.claims.companyId;

        if (!leadId || !companyId) {
          console.error("Missing leadId or companyId in claims");
          return;
        }

        // Fetch program types
        const programTypesSnapshot = await getDocs(collection(db, 'programTypes'));
        const programTypesData = programTypesSnapshot.docs.map(doc => ({
          id: doc.id,
          programType: doc.data().programType
        }));
        setProgramTypes(programTypesData);

        // Fetch countries
        const countriesSnapshot = await getDocs(collection(db, 'countries'));
        const countriesData = countriesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));

        // Fetch current lead data
        const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
        if (leadDoc.exists()) {
          const data = leadDoc.data();

          // Set program
          if (data.programTypeId) {
            setSelectedProgramType(data.programTypeId);
            const program = programTypesData.find(p => p.id === data.programTypeId);
            if (program) setSelectedProgramName(program.programType);
          }

          // Set country
          if (data.destinationId) {
            setSelectedCountry(data.destinationId);
            const country = countriesData.find(c => c.id === data.destinationId);
            if (country) setSelectedCountryName(country.name);
          }

          // Set intake
          setSelectedMonth(data.intakeMonth || '');
          setSelectedYear(data.intakeYear || new Date().getFullYear());

          // Set universities
          if (data.universityIds) {
            setSelectedUniversityIds(data.universityIds);
          }
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchSelectedUniversities = async () => {
      if (selectedUniversityIds.length === 0) {
        setSelectedUniversities([]);
        return;
      }

      try {
        const universitiesData: University[] = [];
        for (const id of selectedUniversityIds) {
          const uniDoc = await getDoc(doc(db, 'universities', id));
          if (uniDoc.exists()) {
            universitiesData.push({
              id: uniDoc.id,
              name: uniDoc.data().name,
              uniCode: uniDoc.data().uniCode
            });
          }
        }
        setSelectedUniversities(universitiesData);
      } catch (error) {
        console.error("Error fetching selected universities:", error);
      }
    };

    fetchSelectedUniversities();
  }, [selectedUniversityIds]);

  useEffect(() => {
    const fetchRelationshipManager = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const idTokenResult = await currentUser.getIdTokenResult();
        const leadId = idTokenResult.claims.leadUid;
        const companyId = idTokenResult.claims.companyId;

        if (!leadId || !companyId) {
          console.error("Missing leadId or companyId in claims");
          return;
        }

        // Get the lead document to find assignedTo
        const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
        if (!leadDoc.exists()) return;

        const assignedTo = leadDoc.data().assignedTo;
        if (!assignedTo) return;

        // Get the user document of the assigned RM
        const userDoc = await getDoc(doc(db, 'users', assignedTo));
        if (!userDoc.exists()) return;

        const userData = userDoc.data();
        setRelationshipManager({
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          linkedin: userData.linkedin
        });
      } catch (error) {
        console.error("Error fetching relationship manager:", error);
      }
    };

    fetchRelationshipManager();
  }, []);

  useEffect(() => {
    const fetchCurrentStage = async () => {
      try {
        setIsLoadingStatus(true);
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const idTokenResult = await currentUser.getIdTokenResult();
        const leadId = idTokenResult.claims.leadUid;
        const companyId = idTokenResult.claims.companyId;

        if (!leadId || !companyId) {
          console.error("Missing leadId or companyId in claims");
          setAdmissionStatus("Process yet to be initiated");
          return;
        }

        // Get the lead document to find admissionStatusId
        const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
        if (!leadDoc.exists()) {
          setAdmissionStatus("Process yet to be initiated");
          return;
        }

        const admissionStatusId = leadDoc.data().admissionStatusId;

        if (!admissionStatusId) {
          setAdmissionStatus("Process yet to be initiated");
          return;
        }

        // Get the admission status document
        const admissionStatusDoc = await getDoc(doc(db, 'leadAdmissionStatus', admissionStatusId));
        if (!admissionStatusDoc.exists()) {
          setAdmissionStatus("Process yet to be initiated");
          return;
        }

        const admissionStatusData = admissionStatusDoc.data() as LeadAdmissionStatus;
        setAdmissionStatus(admissionStatusData.admissionStatus);
      } catch (error) {
        console.error("Error fetching admission status:", error);
        setAdmissionStatus("Process yet to be initiated");
      } finally {
        setIsLoadingStatus(false);
      }
    };

    fetchCurrentStage();
  }, []);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const idTokenResult = await currentUser.getIdTokenResult();
        const leadId = idTokenResult.claims.leadUid;
        const companyId = idTokenResult.claims.companyId;

        if (!leadId || !companyId) {
          console.error("Missing leadId or companyId in claims");
          return;
        }

        const tasksSnapshot = await getDocs(
          query(
            collection(db, `companies/${companyId}/leads/${leadId}/tasks`),
            where('isDone', '==', false),
            orderBy('dueDate', 'asc')
          )
        );

        const tasksData = tasksSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Task[];

        setTasks(tasksData);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    fetchTasks();
  }, []);

  const calculateDaysToGo = (dueDate: Timestamp) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDateObj = dueDate.toDate();
    dueDateObj.setHours(0, 0, 0, 0);

    const diffTime = dueDateObj.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTaskStatus = (dueDate: Timestamp) => {
    const diffDays = calculateDaysToGo(dueDate);

    if (diffDays < 0) {
      return { text: "Overdue", isOverdue: true, isDueToday: false };
    } else if (diffDays === 0) {
      return { text: "Due Today", isOverdue: false, isDueToday: true };
    } else if (diffDays === 1) {
      return { text: "1 day to go", isOverdue: false, isDueToday: false };
    } else {
      return { text: `${diffDays} days to go`, isOverdue: false, isDueToday: false };
    }
  };

  const handleMarkAsDone = async (taskId: string) => {
    try {
      setIsUpdatingTask(true);
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const idTokenResult = await currentUser.getIdTokenResult();
      const leadId = idTokenResult.claims.leadUid;
      const companyId = idTokenResult.claims.companyId;

      if (!leadId || !companyId) {
        console.error("Missing leadId or companyId in claims");
        return;
      }

      await updateDoc(
        doc(db, `companies/${companyId}/leads/${leadId}/tasks/${taskId}`),
        { isDone: true }
      );

      // Update local state
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      setToastMessage('Task Marked as done!');
      setIsErrorToast(false);
      setShowToast(true);
    } catch (error) {
      setToastMessage('Error marking task as done. Please try again.');
      setIsErrorToast(true);
      setShowToast(true);
      console.error("Error marking task as done:", error);
    } finally {
      setIsUpdatingTask(false);
    }
  };

  const handleCountrySearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setSelectedCountryName('');
    setCountrySearchTerm(e.target.value);

    if (value.length < 3) {
      setCountries([]);
      return;
    }

    setIsLoadingCountry(true);
    try {
      const countriesRef = collection(db, 'countries');
      const q = query(
        countriesRef,
        where('name', '>=', value),
        where('name', '<=', value + '\uf8ff'),
        limit(10)
      );

      const querySnapshot = await getDocs(q);
      const countriesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));

      setCountries(countriesData);
    } catch (error) {
      console.error("Error searching countries:", error);
    } finally {
      setIsLoadingCountry(false);
    }
  }

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.length < 3) {
      setUniversities([]);
      return;
    }

    setIsLoadingUniversities(true);
    try {
      // Using Meilisearch instead of Firestore
      const meiliClient = new MeiliSearch({
        host: process.env.REACT_APP_MEILISEARCH_URL || 'https://search.scholarcred.com',
        apiKey: process.env.REACT_APP_MEILISEARCH_API_KEY || '80cdae41311553c99f1835f65ab59832b662cb1a7ac50deb4edd3f520b47bfc1'
      });

      const index = meiliClient.index('universities');
      const searchResults = await index.search(value, {
        limit: 10
      });

      // Format the results to match the existing structure
      const universitiesData = searchResults.hits.map(hit => ({
        id: hit.id as string,
        name: hit.name as string,
        uniCode: '' // Meilisearch data doesn't have uniCode, leave empty
      }));

      setUniversities(universitiesData);
    } catch (error) {
      console.error("Error searching universities:", error);
    } finally {
      setIsLoadingUniversities(false);
    }
  };

  const handleSelectUniversity = (universityId: string) => {
    if (selectedUniversityIds.length < 3 && !selectedUniversityIds.includes(universityId)) {
      setSelectedUniversityIds([...selectedUniversityIds, universityId]);
    }
  };

  const handleRemoveUniversity = (universityId: string) => {
    setSelectedUniversityIds(selectedUniversityIds.filter((id) => id !== universityId));
  };

  const handleSavePreferences = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const idTokenResult = await currentUser.getIdTokenResult();
      const leadId = idTokenResult.claims.leadUid;
      const companyId = idTokenResult.claims.companyId;

      if (!leadId || !companyId) {
        console.error("Missing leadId or companyId in claims");
        return;
      }

      await updateDoc(doc(db, `companies/${companyId}/leads/${leadId}`), {
        programTypeId: selectedProgramType,
        destinationId: selectedCountry,
        intakeMonth: selectedMonth,
        intakeYear: selectedYear,
        universityIds: selectedUniversityIds
      });
      setToastMessage('Preferences saved successfully !');
      setIsErrorToast(false);
      setShowToast(true);
    } catch (error) {
      setToastMessage('Error saving preferences. Please try again.');
      setIsErrorToast(true);
      setShowToast(true);
      console.error("Error saving preferences:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-200">
      <div className="fixed top-16 left-0 h-full z-10">
        <SideNavbar />
      </div>
      <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
        <div className="flex gap-6">
          <div className="w-[60%] bg-white p-4 rounded-3xl shadow-md">
            <div className="flex flex-col">
              <div className="flex items-center justify-start w-full mb-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full mr-4"
                  style={{ background: 'linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)' }}>
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/hugeicons_preference-horizontal.svg`} alt="Preferences Icon" className="w-5 h-5" />
                </div>
                <h2 className="font-montserrat text-xl font-medium text-black">My Preferences</h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="relative" ref={programRef}>
                  <label className="block font-montserrat text-sm text-black mb-1">Preferred Program</label>
                  <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                    <div className="relative">
                      <input type="text"
                        value={selectedProgramName}
                        placeholder="Post Graduation"
                        onClick={() => setIsProgramOpen(!isProgramOpen)}
                        className="w-full py-1.5 pl-8 pr-4 bg-white rounded-3xl cursor-pointer" readOnly />
                      <button type="button"
                        className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <img src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                          alt="Dropdown Arrow" className={`w-6 h-6 transition-transform ${isProgramOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  </div>
                  {isProgramOpen && (
                    <div className="absolute w-full mt-1 bg-white border rounded-3xl shadow-lg z-10">
                      {programTypes.map((program) => (
                        <div key={program.id} className="px-3 py-1.5 hover:bg-gray-100 cursor-pointer text-sm"
                          onClick={() => {
                            setSelectedProgramType(program.id);
                            setSelectedProgramName(program.programType);
                            setIsProgramOpen(false);
                          }}>
                          {program.programType}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block font-montserrat text-sm text-black mb-1">Intake</label>
                  <div className="relative">
                    <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                      <div className="flex">
                        <div className="relative w-1/2">
                          <select
                            value={selectedMonth}
                            onChange={(e) => {
                              setSelectedMonth(e.target.value);
                              handleSavePreferences();
                            }}
                            className="w-full py-1.5 pl-8 pr-8 border-r rounded-l-3xl text-gray-800 appearance-none cursor-pointer"
                          >
                            <option value="">Month</option>
                            {months.map((month) => (
                              <option key={month} value={month}>{month}</option>
                            ))}
                          </select>
                          <img
                            src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                            alt="Dropdown Arrow"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 w-6 h-6 pointer-events-none"
                          />
                        </div>
                        <div className="relative w-1/2">
                          <select
                            value={selectedYear}
                            onChange={(e) => {
                              setSelectedYear(Number(e.target.value));
                              handleSavePreferences();
                            }}
                            className="w-full py-1.5 pl-8 pr-8 rounded-r-3xl text-gray-800 appearance-none cursor-pointer"
                          >
                            <option value="">Year</option>
                            {years.map((year) => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                          <img
                            src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                            alt="Dropdown Arrow"
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 w-6 h-6 pointer-events-none"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative" ref={destinationRef}>
                  <label className="block font-montserrat text-sm text-black mb-1">Destination</label>
                  <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                    <div className="relative">
                      <input type="text"
                        value={countrySearchTerm || selectedCountryName}
                        placeholder="United Kingdom"
                        onChange={handleCountrySearchChange}
                        onClick={() => setIsDestinationOpen(!isDestinationOpen)}
                        className="w-full py-1.5 pl-8 pr-4 bg-white rounded-3xl cursor-pointer" />
                      <button type="button" className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <img src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                          alt="Dropdown Arrow" className={`w-6 h-6 transition-transform ${isDestinationOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  </div>
                  {isDestinationOpen && (
                    <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-20 max-h-[300px] overflow-hidden">
                      <div className="p-2">
                        <div className="space-y-1 max-h-[280px] overflow-y-auto">
                          {isLoadingCountry ? (
                            <div className="px-4 py-2 text-gray-500">Loading...</div>
                          ) : countries.length > 0 ? (
                            countries.map((country) => (
                              <button
                                key={country.id}
                                className="px-4 py-2 text-left hover:bg-gray-100 rounded-xl cursor-pointer text-sm w-full"
                                onClick={() => {
                                  setSelectedCountry(country.id);
                                  setSelectedCountryName(country.name);
                                  setCountrySearchTerm('');
                                  setIsDestinationOpen(false);
                                }}>
                                <div className="flex justify-between items-center text-base">
                                  {country.name}
                                </div>
                              </button>
                            ))
                          ) : countrySearchTerm.length < 3 ? (
                            <div className="px-4 py-2 text-gray-500">Type at least 3 characters to search</div>
                          ) : (
                            <div className="px-4 py-2 text-gray-500">No countries found</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block font-montserrat text-sm text-black mb-1">
                    Preferred Universities
                    <span className="text-xs text-gray-600 text-normal ml-2">(choose up to 3)</span>
                  </label>
                  <div className="relative">
                    <div className="relative">
                      <input
                        ref={universitiesInputRef}
                        type="text"
                        placeholder="Search universities"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onClick={() => setIsUniversityDropdownOpen(true)}
                        className="w-full py-2 pl-8 pr-4 bg-white border border-gray-300 rounded-3xl focus:outline-none focus:border-[#02385E] text-left text-sm"
                      />
                      <img
                        src={`${process.env.PUBLIC_URL}/images/desktop/Nav-Search.svg`}
                        alt="Search"
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                      />
                    </div>

                    {/* Selected Universities Display */}
                    {selectedUniversities.length > 0 && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2">
                          {selectedUniversities.map((university) => (
                            <div
                              key={university.id}
                              className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-800"
                            >
                              {university.name}
                              <button
                                type="button"
                                onClick={() => handleRemoveUniversity(university.id)}
                                className="text-red-500"
                              >
                                <img
                                  src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                  alt="Remove"
                                  className="w-4 h-4"
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Universities Dropdown */}
                    {isUniversityDropdownOpen && (
                      <div
                        ref={universitiesRef}
                        className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-20 max-h-[300px] overflow-hidden"
                      >
                        <div className="p-2">
                          <div className="space-y-1 max-h-[280px] overflow-y-auto">
                            {isLoadingUniversities ? (
                              <div className="px-4 py-2 text-gray-500">Loading...</div>
                            ) : universities.length > 0 ? (
                              universities.map((university) => (
                                <button
                                  key={university.id}
                                  onClick={() => {
                                    if (!selectedUniversityIds.includes(university.id) && selectedUniversityIds.length < 3) {
                                      handleSelectUniversity(university.id);
                                      setSearchTerm('');
                                      setUniversities([]);
                                      setIsUniversityDropdownOpen(false);
                                    }
                                  }}
                                  disabled={selectedUniversityIds.length >= 3 && !selectedUniversityIds.includes(university.id)}
                                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 rounded-xl ${selectedUniversityIds.includes(university.id)
                                    ? 'bg-gray-100'
                                    : ''
                                    } ${selectedUniversityIds.length >= 3 && !selectedUniversityIds.includes(university.id)
                                      ? 'opacity-50 cursor-not-allowed'
                                      : ''
                                    }`}
                                >
                                  <div className="flex justify-between items-center">
                                    <span>{university.name}</span>
                                    <span className="text-gray-500 text-sm">{university.uniCode}</span>
                                  </div>
                                </button>
                              ))
                            ) : searchTerm.length < 3 ? (
                              <div className="px-4 py-2 text-gray-500">Type at least 3 characters to search</div>
                            ) : (
                              <div className="px-4 py-2 text-gray-500">No universities found</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Save Button */}
                <div className="col-span-2 flex justify-end mt-4">
                  <button
                    onClick={handleSavePreferences}
                    className="px-6 py-2 bg-gradient-to-r from-[#3483BE] to-[#02385E] text-white rounded-3xl hover:opacity-90"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="w-[40%] flex flex-col gap-4">
            <div className="bg-white p-4 rounded-3xl shadow-md">
              <h3 className="text-lg font-semibold mb-3">Relationship Manager</h3>
              {relationshipManager ? (
                <div className="bg-gray-100 p-3 rounded-3xl">
                  {relationshipManager.name && (
                    <div className="flex justify-between items-center mb-1">
                      <h4 className="text-base font-semibold"
                        style={{
                          background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}>
                        {relationshipManager.name}
                      </h4>
                      {relationshipManager.linkedin && (
                        <a href={relationshipManager.linkedin} target="_blank" rel="noopener noreferrer">
                          <img
                            src={`${process.env.PUBLIC_URL}/images/desktop/entypo-social_linkedin-with-circle.svg`}
                            className="w-5 h-5 text-gray-600"
                            alt="LinkedIn"
                          />
                        </a>
                      )}
                    </div>
                  )}
                  {relationshipManager.email && (
                    <p className="text-xs text-gray-500 mb-1">{relationshipManager.email}</p>
                  )}
                  {relationshipManager.phone && (
                    <p className="text-sm text-gray-600">{relationshipManager.phone}</p>
                  )}
                </div>
              ) : (
                <div className="bg-gray-100 p-3 rounded-3xl">
                  <p className="text-sm text-gray-500">No relationship manager assigned</p>
                </div>
              )}
            </div>
            <div className="bg-white p-4 rounded-3xl shadow-md">
              <h3 className="text-lg font-semibold mb-3">Current Admission Status</h3>
              {isLoadingStatus ? (
                <div className="animate-pulse">
                  <div className="h-8 w-48 bg-gray-200 rounded-3xl"></div>
                </div>
              ) : (
                <div className="inline-block px-4 py-2 rounded-3xl bg-gray-100 border border-[#02385E]">
                  <span className="font-medium text-[#02385E]">{admissionStatus}</span>
                </div>
              )}
            </div>
          </div>

        </div>
        {/* <div className="w-full mt-6 bg-white p-4 rounded-3xl shadow-md">
          <div className="flex items-center justify-start w-full  px-6">
            <h2 className="font-montserrat text-xl font-medium text-black">Quick Actions - </h2>
            <div className="ml-4">
              {QuickActions.map((action) => (
                <button
                  key={action}
                  onClick={() => handleQuickActionClick(action)}
                  className={`py-2 px-6 rounded-3xl text-sm font-medium transition-colors duration-200 mr-4
            ${activeQuickAction === action
                      ? 'bg-gradient-to-b from-[#02385E] to-[#02385E] text-white'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                >
                  {action}
                </button>
              ))}
            </div>
          </div>
        </div> */}
        <div className="w-full mt-6 bg-white p-6 rounded-3xl shadow-md">
          <div className="flex items-center mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full mr-4"
              style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)' }}>
              <img src={`${process.env.PUBLIC_URL}/images/desktop/solar_history-bold-duotone.svg`} alt="History Icon" className="w-6 h-6" />
            </div>
            <h2 className="font-montserrat text-xl font-medium text-black">Pending Tasks</h2>
          </div>

          <div className="mt-4">
            {tasks.length > 0 ? (
              <div className="space-y-3">
                {tasks.map((task) => (
                  <div key={task.id} className="flex items-center justify-between bg-gray-100 p-3 rounded-3xl">
                    <div className="flex-1">
                      <p className="ml-2 text-sm font-medium text-gray-800">{task.task}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="ml-2 text-xs text-gray-500">
                          Due: {task.dueDate.toDate().toLocaleDateString()}
                        </span>
                        <span
                          className={
                            getTaskStatus(task.dueDate).isOverdue
                              ? 'text-xs font-medium text-red-600'
                              : getTaskStatus(task.dueDate).isDueToday
                                ? 'text-xs font-medium text-orange-500'
                                : 'text-xs font-medium'
                          }
                          style={
                            !getTaskStatus(task.dueDate).isOverdue &&
                              !getTaskStatus(task.dueDate).isDueToday
                              ? { color: '#02385E' }
                              : undefined
                          }
                        >
                          {getTaskStatus(task.dueDate).text}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleMarkAsDone(task.id)}
                      disabled={isUpdatingTask}
                      className="px-3 py-1 text-sm text-white rounded-full bg-[#02385E] hover:bg-[#02385E]/90 disabled:opacity-50"
                    >
                      Mark as Done
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-100 p-3 rounded-3xl">
                <p className="text-sm text-gray-500">No pending tasks</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {showToast && (
        <Toast
          message={toastMessage}
          isError={
            toastMessage.includes('Error') ||
            toastMessage.includes('Cannot') ||
            toastMessage.includes('Failed')
          }
          show={showToast}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default StudentDashboard;